import { toast, ToastOptions, ToastPromiseParams } from 'react-toastify';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { Text, Title } from '@tremor/react';
import { ClipLoader } from 'react-spinners';

const getToastStyles = (type: string) => {
  const baseStyles =
    'relative flex !items-start p-4 rounded-xl border shadow-lg !backdrop-blur-sm ![& button]:text-red-500 transition-all transition-duration-3000 mb-1';

  switch (type) {
    case 'success':
      return `${baseStyles} !bg-green-50/90 !border-green-200 !text-green-800`;
    case 'error':
      return `${baseStyles} !bg-red-50/90 !border-red-200 !text-red-800`;
    case 'warning':
      return `${baseStyles} !bg-yellow-50/90 !border-yellow-200 !text-yellow-800`;
    case 'info':
    case 'default':
      return `${baseStyles} !bg-blue-50/90 !border-blue-200 !text-blue-800`;
    default:
      return `${baseStyles} !bg-gray-50/90 !border-gray-200 !text-gray-800`;
  }
};

const getIcon = (type: string) => {
  const iconClass = 'w-5 h-5 flex-shrink-0 mt-0.5';

  switch (type) {
    case 'success':
      return <CheckCircle className={`${iconClass} text-green-600`} />;
    case 'error':
      return <AlertCircle className={`${iconClass} text-red-600`} />;
    case 'warning':
      return <AlertTriangle className={`${iconClass} text-yellow-600`} />;
    case 'info':
      return <Info className={`${iconClass} text-blue-600`} />;
    default:
      return <Info className={`${iconClass} text-gray-600`} />;
  }
};

const getProgressBarStyles = (type: string) => {
  const baseStyles = `!bg-none`;

  switch (type) {
    case 'success':
      return `${baseStyles} !bg-green-500`;
    case 'error':
      return `${baseStyles} !bg-red-500`;
    case 'warning':
      return `${baseStyles} !bg-yellow-500`;
    case 'info':
      return `${baseStyles} !bg-blue-500`;
    default:
      return `${baseStyles} !bg-gray-500`;
  }
};

type Messages = {
  [K in keyof ToastPromiseParams]: { title?: string; message: string } | string;
};

type Vals = Exclude<NonNullable<Messages[keyof Messages]>, string>;

function getToastTexts(val: Vals | string, defaultTitle: string) {
  const title = typeof val === 'object' && val.title ? val.title : defaultTitle;
  const message = val && typeof val === 'string' ? val : (val as Props).message;

  return [title, message];
}

export function toastPromise<T>(
  promise: Promise<T>,
  { error, pending, success }: Messages,
  options?: ToastOptions,
) {
  const autoClose = typeof options?.autoClose === 'undefined' ? 5000 : options.autoClose;
  return toast.promise(
    promise,
    {
      error: error
        ? {
            render() {
              const [title, message] = getToastTexts(error, 'Error!');
              return <ToastContent title={title} message={message} />;
            },
            autoClose,
          }
        : undefined,
      pending: pending
        ? {
            render() {
              const [title, message] = getToastTexts(pending, 'Updating!');
              return <ToastContent title={title} message={message} />;
            },
          }
        : undefined,
      success: success
        ? {
            render() {
              const [title, message] = getToastTexts(success, 'Success!');
              return <ToastContent title={title} message={message} />;
            },
            autoClose,
          }
        : undefined,
    },
    {
      className(context) {
        return getToastStyles(context?.type || '');
      },
      closeButton({ closeToast }) {
        return (
          <button className="ml-auto" onClick={closeToast}>
            <X size={16} />
          </button>
        );
      },
      icon(props) {
        return props.isLoading ? (
          <ClipLoader
            className="flex-shrink-0 text-blue-600"
            color="rgb(37 99 235 / var(--tw-text-opacity, 1))"
            size={24}
          />
        ) : (
          getIcon(props.type)
        );
      },
      ...options,
      autoClose,
    },
  );
}

export const showToast = (
  title: string,
  message: string,
  type: 'success' | 'error' | 'info' | 'warning' = 'info',
  options?: ToastOptions,
) => {
  toast(<ToastContent title={title} message={message} />, {
    className: getToastStyles(type),
    progressClassName: getProgressBarStyles(type),
    closeButton({ closeToast }) {
      return (
        <button className="ml-auto" onClick={closeToast}>
          <X size={16} />
        </button>
      );
    },
    icon: getIcon(type),
    ...options,
  });
};

type Props = Required<Vals>;

function ToastContent({ title, message }: Props) {
  return (
    <article>
      <Title className="text-sm font-semibold text-inherit mr-6">{title}</Title>
      {message ? (
        <Text className="text-sm opacity-90 mt-1 text-inherit">{message}</Text>
      ) : null}
    </article>
  );
}
